/**
 * The camera end of proof-of-unique-human.
 *
 * This began as FaceKey's speak-to-verify pipeline and no longer uses
 * speech at all. Asking people to read digits aloud turned out to be the
 * single biggest barrier to getting in - it fails on iOS where the
 * microphone cannot be shared with the recogniser, in noisy places, in
 * accents, in languages the device has no recogniser for, and for anyone
 * who cannot speak - while adding nothing a validator could check.
 *
 * What replaces it is a movement challenge derived from a recent DAG tip,
 * so the sequence did not exist before that vertex was issued and cannot
 * be known in advance by anyone.
 *
 * What the check establishes, in layers:
 *   1. A short sequence of head        - beats a photo, a mask, a still.
 *      movements happened, in the
 *      order the network demanded
 *   2. The jaw geometry changed as a    - beats a screen or a print held
 *      solid object's would                 up to the lens.
 *   3. The mouth widened into a smile   - beats a frozen frame.
 *   4. The face descriptor before the   - beats a face swap mid-challenge.
 *      check matches the one after
 *
 * Nothing here leaves the device except the derived code, the band hashes
 * and a handful of summary numbers. No image, no video, no audio, no
 * embedding. See identity.ts for what the code can and cannot reveal, and
 * LivenessProof in tx.ts for why these numbers deter casual abuse rather
 * than prove anything.
 */

import {
  encodeFaceCode, bandHashes, faceSketchBits, l2normalize,
  type Hex,
} from '../../protocol/src/index.ts';
import { toHex } from '../../protocol/src/bytes.ts';
import { ModelIntegrityError, enforceModelIntegrity } from './models.ts';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare const faceapi: any;

/** One movement the network can demand. */
export type ChallengeAction = 'L' | 'R' | 'U' | 'D' | 'S';

export interface ChallengeStep {
  index: number;
  total: number;
  action: ChallengeAction;
}

/** What the wallet tells the person to do. Short, and no jargon. */
export const ACTION_PROMPTS: Record<ChallengeAction, string> = {
  L: 'turn your head to one side',
  R: 'now turn it to the other side',
  U: 'tip your chin up',
  D: 'tip your chin down',
  S: 'smile',
};

const ACTION_FAILURES: Record<ChallengeAction, string> = {
  L: 'No head turn was seen.',
  R: 'No turn to the other side was seen.',
  U: 'No upward tilt was seen.',
  D: 'No downward tilt was seen.',
  S: 'No smile was seen.',
};

export interface FaceConfig {
  /** Where the face-api models live. Point this at a self-hosted copy to
   *  remove the CDN as a dependency - see README, "Running without a CDN". */
  modelUrl: string;
  matchThreshold: number;
  continuityDistance: number;
  framesPerScan: number;
  /** Movement thresholds, in units of interocular distance. Mirrored in
   *  tx.ts so validators check the same numbers. */
  yawDelta: number;
  pitchDelta: number;
  /**
   * How much wider than resting the mouth must get to count as a smile,
   * in units of interocular distance.
   */
  smileDelta: number;
  /**
   * How far the mouth may open and still be a smile rather than a yawn or
   * a word, in the same units.
   */
  maxSmileOpen: number;
  /** Minimum jaw-asymmetry change: the flat-image check. */
  minAsym: number;
  baselineMs: number;
  /** How long one movement may take before the attempt fails. */
  stepTimeoutMs: number;
  /** How long to wait for a return to neutral between movements. */
  settleMs: number;
  /**
   * Detector thresholds.
   *
   * Separated out because the right value depends on what is being
   * looked at. A live camera frame in good light detects at 0.5 without
   * trouble; a person in a dim room, wearing glasses, at an angle, on a
   * cheap front camera, often does not - and telling them there is no
   * face in the frame when they are looking straight at it is the single
   * most alienating thing this pipeline can do.
   */
  detectConfidence: number;
  minDetectionScore: number;
  fastConfidence: number;
  /**
   * How long to keep looking before giving up on finding a face at all.
   *
   * Taking one frame and failing is wrong: the first frame is often
   * taken while the camera is still adjusting exposure. Waiting a few
   * seconds and saying what to do costs nothing and turns most failures
   * into successes.
   */
  acquireMs: number;
}

export const DEFAULT_FACE_CONFIG: FaceConfig = {
  modelUrl: 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.15/model/',
  matchThreshold: 0.45,
  continuityDistance: 0.45,
  framesPerScan: 12,
  // Generous enough for a small movement on a phone held at arm's length,
  // large enough that ordinary tremor does not satisfy a step by itself.
  yawDelta: 0.20,
  pitchDelta: 0.13,
  smileDelta: 0.07,
  maxSmileOpen: 0.30,
  minAsym: 0.035,
  baselineMs: 800,
  stepTimeoutMs: 6_000,
  settleMs: 1_500,
  detectConfidence: 0.5,
  minDetectionScore: 0.55,
  fastConfidence: 0.4,
  acquireMs: 6_000,
};

export interface LivenessMetrics {
  /**
   * How the face was captured.
   *
   * 'motion' means the movement challenge was performed and passed.
   * 'capture' means it was not: the frames were simply averaged. The two
   * are kept distinguishable because a record made without a liveness
   * check must be identifiable for the life of the wallet, not just on
   * the day it was made.
   */
  mode: 'motion' | 'capture';
  challenge: string;
  steps: number;
  yawMilli: number;
  pitchMilli: number;
  parallaxMilli: number;
  smiles: number;
  frames: number;
}

export interface ScanResult {
  ok: boolean;
  reason?: string;
  /** 48-byte cancelable code, hex. */
  code?: Hex;
  bands?: Hex[];
  /**
   * Wider projection bits used to enrol or reproduce the wallet-recovery
   * secret. Stays in memory: the *helper* derived from these is public,
   * these are not published directly.
   */
  sketchBits?: Uint8Array;
  metrics?: LivenessMetrics;
  /** Kept in memory only, for local re-recognition. Never transmitted. */
  embedding?: Float64Array;
}

export interface ScanProgress {
  (stage: 'models' | 'camera' | 'baseline' | 'listening' | 'capturing' | 'done', detail: string): void;
}

const pdist = (p: { x: number; y: number }, q: { x: number; y: number }) => Math.hypot(p.x - q.x, p.y - q.y);

type Pt = { x: number; y: number };

const mean = (pts: Pt[]): Pt => ({
  x: pts.reduce((a, p) => a + p.x, 0) / pts.length,
  y: pts.reduce((a, p) => a + p.y, 0) / pts.length,
});

/**
 * Head pose from 68 landmarks, without a 3-D model.
 *
 * Everything is measured in units of interocular distance and projected
 * onto the eye axis, so the numbers are invariant to how far away the
 * person is and to head roll. That matters: a threshold in pixels would
 * mean something different on every phone.
 */
export interface Pose {
  /** Nose displacement along the eye axis. Turning the head moves it. */
  yaw: number;
  /** Nose displacement perpendicular to it. Nodding moves it. */
  pitch: number;
  /**
   * Mouth width along the eye axis, in units of interocular distance.
   *
   * A smile widens the mouth, and does so in a direction the eye axis
   * makes roll-invariant. Blink detection used to fill this slot and was
   * replaced: eye aspect ratio depends on eyelid pixels a few frames wide,
   * which a phone camera in ordinary light frequently fails to resolve, so
   * people who *were* blinking were told they had not.
   */
  mouthWidth: number;
  /** Inner-lip opening, same units. Separates a smile from a yawn. */
  mouthOpen: number;
  /** Interocular distance in pixels - a proxy for how close the face is. */
  scale: number;
  /**
   * Jaw asymmetry about the nose, normalised by face width.
   *
   * A heuristic, not a proof: turning a real head compresses the far side
   * of the jaw far more than the near side, while tilting a flat photo or
   * a screen changes both together. Watching this move independently of
   * the eye-line measure is what separates "a picture was waved about"
   * from "a head turned".
   */
  asym: number;
}

export function poseOf(pts: Pt[]): Pose {
  const leftEye = pts.slice(36, 42);
  const rightEye = pts.slice(42, 48);
  const lc = mean(leftEye);
  const rc = mean(rightEye);
  const nose = pts[30];

  const scale = pdist(lc, rc) || 1;
  // Unit vector along the eye line, and its perpendicular.
  const ax = (rc.x - lc.x) / scale;
  const ay = (rc.y - lc.y) / scale;
  const mid = { x: (lc.x + rc.x) / 2, y: (lc.y + rc.y) / 2 };
  const dx = nose.x - mid.x;
  const dy = nose.y - mid.y;

  const jawL = pts[0];
  const jawR = pts[16];
  const faceWidth = pdist(jawL, jawR) || 1;

  return {
    yaw: (dx * ax + dy * ay) / scale,
    pitch: (dx * -ay + dy * ax) / scale,
    mouthWidth: Math.abs((pts[54].x - pts[48].x) * ax + (pts[54].y - pts[48].y) * ay) / scale,
    mouthOpen: pdist(pts[62], pts[66]) / scale,
    scale,
    asym: (pdist(nose, jawL) - pdist(nose, jawR)) / faceWidth,
  };
}

function descriptorDistance(a: ArrayLike<number>, b: ArrayLike<number>): number {
  let s = 0;
  for (let i = 0; i < a.length; i++) {
    const d = a[i] - b[i];
    s += d * d;
  }
  return Math.sqrt(s);
}

function averageDescriptors(list: Float64Array[]): Float64Array {
  const out = new Float64Array(list[0].length);
  for (const v of list) for (let i = 0; i < v.length; i++) out[i] += v[i];
  for (let i = 0; i < out.length; i++) out[i] /= list.length;
  return l2normalize(out);
}

const median = (a: number[]): number => [...a].sort((x, y) => x - y)[Math.floor(a.length / 2)];

export class FaceScanner {
  private cfg: FaceConfig;
  private video: HTMLVideoElement;
  private overlay: HTMLCanvasElement | null;
  private stream: MediaStream | null = null;
  private ready = false;
  private busy = false;

  constructor(video: HTMLVideoElement, overlay: HTMLCanvasElement | null, cfg: Partial<FaceConfig> = {}) {
    this.video = video;
    this.overlay = overlay;
    this.cfg = { ...DEFAULT_FACE_CONFIG, ...cfg };
  }

  get isReady(): boolean {
    return this.ready;
  }

  async init(onProgress?: ScanProgress): Promise<void> {
    if (this.ready) return;
    onProgress?.('models', 'loading face models');
    if (typeof faceapi === 'undefined') {
      throw new Error('The face models could not be downloaded. Check your connection and reload.');
    }
    // Hash every weight file as it arrives. A substituted model returns
    // whatever embedding its author chose, which is a route to minting
    // identities and to claiming other people's faces.
    enforceModelIntegrity(this.cfg.modelUrl);
    try {
      await Promise.all([
        faceapi.nets.ssdMobilenetv1.loadFromUri(this.cfg.modelUrl),
        faceapi.nets.faceLandmark68Net.loadFromUri(this.cfg.modelUrl),
        faceapi.nets.faceRecognitionNet.loadFromUri(this.cfg.modelUrl),
        faceapi.nets.tinyFaceDetector.loadFromUri(this.cfg.modelUrl),
      ]);
    } catch (e) {
      // The underlying failure is usually a TensorFlow backend that could
      // not start - no WebGL, and the WASM fallback blocked or unfetchable.
      // Its own message names internals no user can act on.
      if (e instanceof ModelIntegrityError) throw e;
      const raw = String((e as Error)?.message ?? '');
      if (/backend|wasm|webgl/i.test(raw)) {
        throw new Error('This browser could not start the face engine. Try Chrome or Safari with hardware acceleration enabled.');
      }
      throw new Error('The face models could not be downloaded. Check your connection and reload.');
    }

    // Camera only. No microphone is requested, which removes an entire
    // class of failure: permission prompts, iOS's refusal to share the mic
    // with speech recognition, noisy rooms, accents, languages the device
    // has no recogniser for, and people who cannot speak.
    onProgress?.('camera', 'requesting camera');
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: { width: 640, height: 480, facingMode: 'user' },
    });
    this.video.srcObject = this.stream;
    await new Promise<void>((r) => {
      if (this.video.readyState >= 1) return r();
      this.video.onloadedmetadata = () => r();
    });
    await this.video.play().catch(() => undefined);

    if (this.overlay) {
      this.overlay.width = this.video.videoWidth || 640;
      this.overlay.height = this.video.videoHeight || 480;
    }
    this.ready = true;
    onProgress?.('done', 'ready');
  }

  stop(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.ready = false;
  }

  private draw(box: { x: number; y: number; width: number; height: number } | null, color: string): void {
    if (!this.overlay) return;
    const ctx = this.overlay.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, this.overlay.width, this.overlay.height);
    if (!box) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.strokeRect(box.x, box.y, box.width, box.height);
  }

  private async frame(withDescriptor: boolean, fast: boolean) {
    const opts = fast
      ? new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: this.cfg.fastConfidence })
      : new faceapi.SsdMobilenetv1Options({ minConfidence: this.cfg.detectConfidence });
    let q = faceapi.detectSingleFace(this.video, opts).withFaceLandmarks();
    if (withDescriptor) q = q.withFaceDescriptor();
    const det = await q;
    if (!det || det.detection.score < (fast ? this.cfg.fastConfidence : this.cfg.minDetectionScore)) return null;
    return {
      box: det.detection.box,
      pts: det.landmarks.positions as { x: number; y: number }[],
      desc: det.descriptor ? l2normalize(Array.from(det.descriptor as Float32Array)) : null,
    };
  }

  /** Is a face in frame right now? Drives the "ready" indicator. */
  async detectPresence(): Promise<boolean> {
    if (!this.ready) return false;
    const det = await faceapi.detectSingleFace(this.video, new faceapi.SsdMobilenetv1Options({ minConfidence: this.cfg.detectConfidence }));
    this.draw(det ? det.box : null, '#34d399');
    return !!det;
  }

  /**
   * Full enrolment scan against a network-issued challenge.
   *
   * No microphone, no speech recognition, no language. The network names a
   * short sequence of head movements derived from a DAG tip, and the
   * camera watches them happen in order. That defeats a still photograph
   * (it cannot move), a recorded video (it will not contain this
   * particular sequence, one of 625, chosen after the recording was made)
   * and a screen held up to the lens (the jaw geometry of a flat image
   * does not change the way a real head's does).
   *
   * It does not defeat a real-time deepfake, and it is not verifiable by
   * anyone but this device. See LivenessProof in tx.ts.
   *
   * @param challenge e.g. "LRUB", derived by the network from a DAG tip.
   */
  async scan(
    challenge: string,
    onProgress?: ScanProgress,
    onStep?: (step: ChallengeStep) => void,
  ): Promise<ScanResult> {
    if (!this.ready) return { ok: false, reason: 'scanner is not initialised' };
    if (this.busy) return { ok: false, reason: 'a scan is already running' };
    this.busy = true;
    try {
      // Keep looking rather than failing on the first frame. A front
      // camera is usually still adjusting exposure when the first frame
      // is taken, and a person who is plainly looking at the lens should
      // never be told there is no face in the picture.
      onProgress?.('baseline', 'Bring your face into the ring');
      const first = await this.acquire(onProgress);
      if (!first?.desc) {
        return {
          ok: false,
          reason: 'We could not see a face clearly. Move somewhere brighter, hold the phone at arm\u2019s length, and try again.',
        };
      }

      // 1. Neutral baseline. Every threshold below is relative to this
      //    person's resting pose, so a naturally asymmetric face is not a
      //    failure and a very still one is not a pass.
      onProgress?.('baseline', 'hold still and look at the camera');
      const basePoses: Pose[] = [];
      const t0 = Date.now();
      while (Date.now() - t0 < this.cfg.baselineMs) {
        const f = await this.frame(false, true);
        if (f) {
          basePoses.push(poseOf(f.pts));
          this.draw(f.box, '#fbbf24');
        }
      }
      if (basePoses.length < 4) return { ok: false, reason: 'Face lost while measuring. Hold the camera steady and try again.' };
      const neutral = {
        yaw: median(basePoses.map((p) => p.yaw)),
        pitch: median(basePoses.map((p) => p.pitch)),
        mouthWidth: median(basePoses.map((p) => p.mouthWidth)),
        mouthOpen: median(basePoses.map((p) => p.mouthOpen)),
        asym: median(basePoses.map((p) => p.asym)),
      };

      // 2. Walk the challenge. Directions are self-calibrating: the first
      //    turn fixes which physical side counts as "L", and the rest must
      //    then be consistent. That keeps the sequence meaningful without
      //    depending on a mirror convention being right on every device.
      let turnSign = 0;
      let pitchSign = 0;
      let maxYaw = 0;
      let maxPitch = 0;
      let maxAsym = 0;
      let smiles = 0;
      let frames = 0;
      let steps = 0;

      const track = (p: Pose): void => {
        frames++;
        maxYaw = Math.max(maxYaw, Math.abs(p.yaw - neutral.yaw));
        maxPitch = Math.max(maxPitch, Math.abs(p.pitch - neutral.pitch));
        maxAsym = Math.max(maxAsym, Math.abs(p.asym - neutral.asym));
      };

      /** Hold the camera open until `done` says the step happened. */
      const awaitCondition = async (
        done: (p: Pose) => boolean,
        timeoutMs: number,
      ): Promise<boolean> => {
        const until = Date.now() + timeoutMs;
        while (Date.now() < until) {
          const f = await this.frame(false, true);
          if (f) {
            const p = poseOf(f.pts);
            track(p);
            this.draw(f.box, '#fbbf24');
            if (done(p)) return true;
          }
          await new Promise((r) => setTimeout(r, 10));
        }
        return false;
      };

      for (let i = 0; i < challenge.length; i++) {
        const action = challenge[i] as ChallengeAction;
        onStep?.({ index: i, total: challenge.length, action });
        onProgress?.('listening', ACTION_PROMPTS[action] ?? 'follow the prompt');

        let ok = false;
        if (action === 'L' || action === 'R') {
          ok = await awaitCondition((p) => {
            const d = p.yaw - neutral.yaw;
            if (Math.abs(d) < this.cfg.yawDelta) return false;
            const sign = d > 0 ? 1 : -1;
            if (turnSign === 0) {
              // First turn of the session fixes the mapping.
              turnSign = action === 'L' ? sign : -sign;
              return true;
            }
            return sign === (action === 'L' ? turnSign : -turnSign);
          }, this.cfg.stepTimeoutMs);
        } else if (action === 'U' || action === 'D') {
          ok = await awaitCondition((p) => {
            const d = p.pitch - neutral.pitch;
            if (Math.abs(d) < this.cfg.pitchDelta) return false;
            const sign = d > 0 ? 1 : -1;
            if (pitchSign === 0) {
              pitchSign = action === 'U' ? sign : -sign;
              return true;
            }
            return sign === (action === 'U' ? pitchSign : -pitchSign);
          }, this.cfg.stepTimeoutMs);
        } else {
          // A smile widens the mouth well past this person's resting width
          // without opening it far - which is what separates it from a
          // yawn, a word, or a jaw held open at a photograph.
          ok = await awaitCondition((p) => {
            const wider = p.mouthWidth > neutral.mouthWidth + this.cfg.smileDelta;
            const notAgape = p.mouthOpen < neutral.mouthOpen + this.cfg.maxSmileOpen;
            if (wider && notAgape) {
              smiles++;
              return true;
            }
            return false;
          }, this.cfg.stepTimeoutMs);
        }

        if (!ok) {
          return { ok: false, reason: `${ACTION_FAILURES[action]} Try again - the movements change every time.` };
        }
        steps++;

        // Settle back towards neutral so one long hold cannot satisfy two
        // steps in a row.
        await awaitCondition(
          (p) => Math.abs(p.yaw - neutral.yaw) < this.cfg.yawDelta * 0.5 &&
                 Math.abs(p.pitch - neutral.pitch) < this.cfg.pitchDelta * 0.5,
          this.cfg.settleMs,
        );
      }

      // The flat-image check compares how the two sides of the jaw move
      // as the head turns, so it only means anything if a turn was asked
      // for. Applying it to a sequence of, say, "smile then chin up"
      // fails a real person for not doing something nobody asked them to
      // do - which is the worst kind of rejection, because there is no
      // way to act on it.
      const askedForATurn = /[LR]/.test(challenge);
      if (askedForATurn && maxAsym < this.cfg.minAsym) {
        return {
          ok: false,
          reason: 'That looked like a flat image rather than a face. Point the camera at yourself, not at a photo or a screen.',
        };
      }

      // 3. Continuity: the same face throughout, so nobody swaps in
      //    halfway through the sequence.
      const after = await this.acquire(onProgress);
      if (!after?.desc) return { ok: false, reason: 'We lost your face at the end of the check. Try again.' };
      if (descriptorDistance(after.desc, first.desc) > this.cfg.continuityDistance) {
        return { ok: false, reason: 'A different face appeared during the check.' };
      }

      // 4. Capture. Averaging many frames is what makes the code stable
      //    enough that the same person reproduces it on a different day.
      onProgress?.('capturing', 'hold steady');
      const descriptors: Float64Array[] = [first.desc, after.desc];
      let attempts = 0;
      while (descriptors.length < this.cfg.framesPerScan && attempts < this.cfg.framesPerScan * 4) {
        attempts++;
        const f = await this.frame(true, false);
        if (f?.desc) {
          if (descriptorDistance(f.desc, first.desc) > this.cfg.continuityDistance) {
            return { ok: false, reason: 'The face changed during capture. Try again.' };
          }
          descriptors.push(f.desc);
          this.draw(f.box, '#34d399');
        }
        await new Promise((r) => setTimeout(r, 50));
      }
      if (descriptors.length < this.cfg.framesPerScan / 2) {
        return { ok: false, reason: 'Not enough clear frames. Improve the lighting and try again.' };
      }

      const embedding = averageDescriptors(descriptors);
      const code = encodeFaceCode(embedding);
      onProgress?.('done', 'verified');
      return {
        ok: true,
        code: toHex(code),
        bands: bandHashes(code),
        sketchBits: faceSketchBits(embedding),
        embedding,
        metrics: {
          mode: 'motion',
          challenge,
          steps,
          yawMilli: Math.round(maxYaw * 1000),
          pitchMilli: Math.round(maxPitch * 1000),
          parallaxMilli: Math.round(maxAsym * 1000),
          smiles,
          frames,
        },
      };
    } catch (e) {
      return { ok: false, reason: (e as Error).message || 'scan failed' };
    } finally {
      this.busy = false;
      this.draw(null, '');
    }
  }

  /**
   * Keep taking frames until a face appears, or the budget runs out.
   *
   * Progress is reported as it goes so the person is told what to change
   * rather than left staring at a still ring.
   */
  private async acquire(onProgress?: ScanProgress) {
    const until = Date.now() + this.cfg.acquireMs;
    let attempts = 0;
    while (Date.now() < until) {
      const f = await this.frame(true, false);
      if (f?.desc) {
        this.draw(f.box, '#34d399');
        return f;
      }
      attempts++;
      if (attempts === 6) onProgress?.('baseline', 'Move a little closer, and into better light');
      if (attempts === 14) onProgress?.('baseline', 'Take off anything covering your face, and hold still');
      this.draw(null, '');
      await new Promise((r) => setTimeout(r, 60));
    }
    return null;
  }

  /**
   * Capture a face code with no liveness check at all.
   *
   * This produces the same 48-byte code as a full scan and establishes
   * *nothing* about whether a living person was in front of the camera:
   * a photograph held up to the lens passes it. It exists so that the
   * product can be demonstrated on a machine where the movement
   * challenge is impractical, and every result is stamped
   * `mode: 'capture'` so that a wallet enrolled this way is identifiable
   * afterwards, in the compliance console and in the database.
   *
   * Nothing should call this unless a deployment has explicitly turned
   * liveness off, and no deployment holding real money should do that.
   */
  async capture(onProgress?: ScanProgress): Promise<ScanResult> {
    if (!this.ready) return { ok: false, reason: 'scanner is not initialised' };
    if (this.busy) return { ok: false, reason: 'a scan is already running' };
    this.busy = true;
    try {
      onProgress?.('baseline', 'Look at the camera and hold still');
      const first = await this.acquire(onProgress);
      if (!first?.desc) {
        return {
          ok: false,
          reason: 'We could not see a face clearly. Move somewhere brighter, hold the phone at arm\u2019s length, and try again.',
        };
      }

      // Averaging many frames is what makes the code stable enough that
      // the same person reproduces it on a different day. That part is
      // unchanged: only the liveness challenge is skipped.
      onProgress?.('capturing', 'Hold steady');
      const descriptors: Float64Array[] = [first.desc];
      let attempts = 0;
      while (descriptors.length < this.cfg.framesPerScan && attempts < this.cfg.framesPerScan * 5) {
        attempts++;
        const f = await this.frame(true, false);
        if (f?.desc) {
          // Still refused if the face changes mid-capture. Averaging two
          // different people would produce a code belonging to neither.
          if (descriptorDistance(f.desc, first.desc) > this.cfg.continuityDistance) {
            return { ok: false, reason: 'The face changed during capture. Try again.' };
          }
          descriptors.push(f.desc);
          this.draw(f.box, '#34d399');
        }
        await new Promise((r) => setTimeout(r, 40));
      }
      if (descriptors.length < Math.max(3, this.cfg.framesPerScan / 2)) {
        return { ok: false, reason: 'Not enough clear frames. Improve the lighting and try again.' };
      }

      const embedding = averageDescriptors(descriptors);
      const code = encodeFaceCode(embedding);
      onProgress?.('done', 'captured');
      return {
        ok: true,
        code: toHex(code),
        bands: bandHashes(code),
        sketchBits: faceSketchBits(embedding),
        embedding,
        metrics: {
          mode: 'capture',
          challenge: '',
          steps: 0,
          yawMilli: 0,
          pitchMilli: 0,
          parallaxMilli: 0,
          smiles: 0,
          frames: descriptors.length,
        },
      };
    } catch (e) {
      return { ok: false, reason: (e as Error).message || 'capture failed' };
    } finally {
      this.busy = false;
      this.draw(null, '');
    }
  }

  /** Re-verify a returning user locally against a stored embedding. */
  async verifyAgainst(stored: Float64Array): Promise<{ ok: boolean; distance: number }> {
    const f = await this.frame(true, false);
    if (!f?.desc) return { ok: false, distance: Infinity };
    const d = descriptorDistance(f.desc, stored);
    return { ok: d < this.cfg.matchThreshold, distance: d };
  }
}
